import {apiClient, queryClient} from '@common/http/query-client';
import {useQuery} from '@tanstack/react-query';
import {PolicyFailReason} from '@common/billing/upgrade/policy-fail-reason';
import {AiTopupSummary} from '@app/editor/ai/usage/use-ai-usage-topups';

interface ResourceStatus {
  allowed: boolean;
  failReason?: PolicyFailReason;
}

interface ResourceUsage {
  used: number;
  total: number;
  create: ResourceStatus;
  update: ResourceStatus;
  delete: ResourceStatus;
}

interface AiUsage {
  hasPermission: boolean;
  used: number;
  total: number | null;
  tokens: {
    used: number;
    total: number | null;
  };
  requests: {
    day: {used: number; total: number | null};
    month: {used: number; total: number | null};
  };
  failReason?: PolicyFailReason;
  limitReason?: 'overTokenLimit' | 'overDailyLimit' | 'overMonthlyLimit' | null;
}

interface Response {
  ai: {
    text: AiUsage;
    images: AiUsage;
    topups?: AiTopupSummary;
    capabilities?: {
      planMode: AiCapability;
      backgroundTasks: AiCapability & {concurrent?: number};
      skills: AiCapability;
      github: AiCapability;
      agentModes: {
        lite: AiCapability;
        economy: AiCapability;
        power: AiCapability;
      };
      attachments: {
        maxPerMessage: number;
        maxSizeMb: number;
      };
    };
  };
  projects: ResourceUsage & {
    customCode: boolean;
    download: boolean;
    export: boolean;
  };
  custom_domains: ResourceUsage;
  uploads: {
    used: number;
    total: number;
  };
}

interface AiCapability {
  allowed: boolean;
  permission?: string;
  failReason?: PolicyFailReason | null;
}

export function useAccountUsage() {
  return useQuery({
    queryKey: ['account-usage'],
    queryFn: () => fetchUsage(),
  });
}

export function reloadAccountUsage() {
  return queryClient.invalidateQueries({
    queryKey: ['account-usage'],
  });
}

function fetchUsage() {
  return apiClient
    .get<Response>(`account/usage`)
    .then(response => response.data);
}
